/**
 * Geokódování: hledání adres a míst.
 *  1. souřadnice zadané přímo textem („50.08, 14.43"),
 *  2. online Nominatim (OpenStreetMap) — pokud je povolen a dostupný,
 *  3. offline fallback: vestavěný seznam českých měst a známých míst.
 *
 * Žádný klíč není potřeba; chyby služby nikdy nepropagujeme jako pád.
 */
import type { LatLng } from '../core/types';

export interface GeocodeHit {
  name: string;
  detail: string;
  lat: number;
  lng: number;
  source: 'coords' | 'online' | 'offline';
}

export interface GeocodeResponse {
  hits: GeocodeHit[];
  /** Vysvětlení stavu (např. „offline výsledky — služba nedostupná"). */
  notice?: string;
}

/** Vestavěná offline databáze míst (souřadnice center). */
const OFFLINE_PLACES: Array<[string, string, number, number]> = [
  ['Praha', 'hlavní město', 50.0875, 14.4213],
  ['Praha hlavní nádraží', 'nádraží, Praha', 50.0830, 14.4355],
  ['Václavské náměstí', 'Praha 1', 50.0813, 14.4280],
  ['Staroměstské náměstí', 'Praha 1', 50.0875, 14.4214],
  ['Anděl', 'Praha 5', 50.0709, 14.4043],
  ['Letiště Václava Havla', 'Praha 6', 50.1008, 14.2632],
  ['Brno', 'Jihomoravský kraj', 49.1951, 16.6068],
  ['Brno hlavní nádraží', 'nádraží, Brno', 49.1906, 16.6128],
  ['Ostrava', 'Moravskoslezský kraj', 49.8209, 18.2625],
  ['Plzeň', 'Plzeňský kraj', 49.7384, 13.3736],
  ['Liberec', 'Liberecký kraj', 50.7663, 15.0543],
  ['Olomouc', 'Olomoucký kraj', 49.5938, 17.2509],
  ['České Budějovice', 'Jihočeský kraj', 48.9745, 14.4743],
  ['Hradec Králové', 'Královéhradecký kraj', 50.2092, 15.8328],
  ['Ústí nad Labem', 'Ústecký kraj', 50.6607, 14.0322],
  ['Pardubice', 'Pardubický kraj', 50.0343, 15.7812],
  ['Zlín', 'Zlínský kraj', 49.2265, 17.6707],
  ['Havířov', 'Moravskoslezský kraj', 49.7798, 18.4368],
  ['Kladno', 'Středočeský kraj', 50.1473, 14.1028],
  ['Most', 'Ústecký kraj', 50.5031, 13.6362],
  ['Karlovy Vary', 'Karlovarský kraj', 50.2306, 12.8702],
  ['Jihlava', 'Kraj Vysočina', 49.3961, 15.5912],
  ['Karlův most', 'Praha 1', 50.0865, 14.4114],
  ['Pražský hrad', 'Praha 1', 50.0900, 14.4002],
  ['Kutná Hora', 'Středočeský kraj', 49.9484, 15.2681],
  ['Český Krumlov', 'Jihočeský kraj', 48.8127, 14.3175],
  ['Špindlerův Mlýn', 'Krkonoše', 50.7264, 15.6094],
  ['Lipno nad Vltavou', 'Jihočeský kraj', 48.6390, 14.2280],
  ['Bratislava', 'Slovensko', 48.1486, 17.1077],
  ['Vídeň', 'Rakousko', 48.2082, 16.3738],
  ['Drážďany', 'Německo', 51.0504, 13.7373],
];

/** Zkusí rozpoznat souřadnice v textu: „50.08, 14.43", „50.08 14.43", „N50.08 E14.43". */
export function parseCoordinates(text: string): LatLng | null {
  const cleaned = text
    .trim()
    .replace(/[NnSsEeWw]/g, ' ')
    .replace(/[;]/g, ',');
  const m = /^\s*(-?\d{1,3}(?:[.,]\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:[.,]\d+)?)\s*$/.exec(cleaned);
  if (!m) return null;
  const norm = (s: string): number => Number(s.replace(',', '.'));
  // Pozor: „50,08 14,43" s čárkami jako desetinnými — regex výše zachytí jen
  // jednoznačné případy; desetinnou čárku podporujeme u mezerou oddělené dvojice.
  const lat = norm(m[1]);
  const lng = norm(m[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/** Bez diakritiky a velkých písmen (odstraní kombinující znaky U+0300–U+036F). */
function normalize(s: string): string {
  const decomposed = s.toLowerCase().normalize('NFD');
  let out = '';
  for (const ch of decomposed) {
    const code = ch.charCodeAt(0);
    if (code < 0x0300 || code > 0x036f) out += ch;
  }
  return out;
}

export function offlineSearch(query: string, limit = 6): GeocodeHit[] {
  const q = normalize(query);
  if (q.length < 2) return [];
  return OFFLINE_PLACES.filter(([name]) => normalize(name).includes(q))
    .slice(0, limit)
    .map(([name, detail, lat, lng]) => ({ name, detail, lat, lng, source: 'offline' as const }));
}

export interface GeocoderOptions {
  /** Povolit online dotazy (Nominatim). */
  online: boolean;
  /** Vlastní endpoint (výchozí veřejný Nominatim). */
  endpoint?: string;
  timeoutMs?: number;
}

/**
 * Hlavní vyhledání. Pořadí: souřadnice → online → offline.
 * Online selhání je tiché — vrátí offline výsledky s vysvětlením.
 */
export async function geocode(query: string, opts: GeocoderOptions): Promise<GeocodeResponse> {
  const q = query.trim();
  if (q.length === 0) return { hits: [] };

  const coords = parseCoordinates(q);
  if (coords) {
    return {
      hits: [
        {
          name: `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`,
          detail: 'zadané souřadnice',
          lat: coords.lat,
          lng: coords.lng,
          source: 'coords',
        },
      ],
    };
  }

  if (opts.online) {
    try {
      const base = opts.endpoint || 'https://nominatim.openstreetmap.org';
      const url = `${base}/search?format=jsonv2&limit=6&accept-language=cs&q=${encodeURIComponent(q)}`;
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 6000);
      const res = await fetch(url, {
        signal: ctrl.signal,
        headers: { Accept: 'application/json' },
      });
      clearTimeout(timer);
      if (res.ok) {
        const data = (await res.json()) as Array<{
          display_name?: string;
          name?: string;
          lat: string;
          lon: string;
          type?: string;
        }>;
        const hits: GeocodeHit[] = [];
        for (const d of data) {
          const lat = Number(d.lat);
          const lng = Number(d.lon);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
          const display = d.display_name ?? d.name ?? q;
          const [first, ...rest] = display.split(',');
          hits.push({
            name: (d.name && d.name.length > 0 ? d.name : first).trim(),
            detail: rest.slice(0, 3).join(',').trim(),
            lat,
            lng,
            source: 'online',
          });
        }
        if (hits.length > 0) return { hits };
        const off = offlineSearch(q);
        return {
          hits: off,
          notice: off.length > 0 ? 'Adresa nenalezena online — nabízím vestavěná místa.' : 'Nic jsme nenašli. Zkuste jiný zápis, nebo zadejte souřadnice „50.08, 14.43".',
        };
      }
    } catch {
      // spadneme na offline větev níže
    }
    const off = offlineSearch(q);
    return {
      hits: off,
      notice:
        off.length > 0
          ? 'Vyhledávací služba je nedostupná — offline výsledky (vestavěná místa).'
          : 'Vyhledávací služba je nedostupná. Zadejte souřadnice „50.08, 14.43", nebo klikněte do mapy.',
    };
  }

  const off = offlineSearch(q);
  return {
    hits: off,
    notice:
      off.length > 0
        ? 'Online hledání je vypnuté — vestavěná místa.'
        : 'Online hledání je vypnuté. Zadejte souřadnice, nebo klikněte do mapy.',
  };
}

/** Reverzní geokódování pro popisek bodu — best effort, nikdy neblokuje. */
export async function reverseGeocode(
  p: LatLng,
  opts: GeocoderOptions,
): Promise<string | null> {
  if (!opts.online) return null;
  try {
    const base = opts.endpoint || 'https://nominatim.openstreetmap.org';
    const url = `${base}/reverse?format=jsonv2&accept-language=cs&lat=${p.lat}&lon=${p.lng}&zoom=17`;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 5000);
    const res = await fetch(url, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
    clearTimeout(timer);
    if (!res.ok) return null;
    const data = (await res.json()) as { display_name?: string };
    if (!data.display_name) return null;
    return data.display_name.split(',').slice(0, 2).join(',').trim();
  } catch {
    return null;
  }
}
